<template>
  <div>
    <a-card style="margin-bottom: 16px">
      <a-row :gutter="16" align="middle">
        <a-col>
          <a-button :loading="loading" @click="reload">手动刷新</a-button>
        </a-col>
        <a-col>
          <a-button
            type="primary"
            :loading="syncing"
            title="解析每个大区域名的最新 IP、探测 sessiond 拿游戏服列表；有变更则更新 IP 并推送给所有该大区 active proxy"
            @click="askSync"
          >同步大区</a-button>
        </a-col>
        <a-col>
          <a-select
            v-model:value="selectedRegionIds"
            mode="multiple"
            allow-clear
            placeholder="筛选大区"
            style="min-width: 240px"
            :max-tag-count="2"
          >
            <a-select-option v-for="r in allRegions" :key="r.region_id" :value="r.region_id">
              {{ r.region_name }}
            </a-select-option>
          </a-select>
        </a-col>
        <a-col>
          <a-input
            v-model:value="serverQuery"
            allow-clear
            placeholder="搜索游戏服 (如:壮志 / 雪原)"
            style="width: 240px"
          />
        </a-col>
        <a-col flex="auto" />
        <a-col style="color: #6b7280; font-size: 12px">
          <span>显示 {{ filteredRegions.length }}/{{ allRegions.length }} 个大区 · {{ totalServers }} 个游戏服 · {{ totalGameIPs }} 个游戏 IP</span>
        </a-col>
      </a-row>
    </a-card>

    <a-empty v-if="!loading && allRegions.length === 0" description="暂无大区数据" />

    <div v-for="r in filteredRegions" :key="r.region_id">
      <a-card style="margin-bottom: 12px; border-left: 4px solid #3b82f6" size="small">
        <template #title>
          <a-row :gutter="12" align="middle">
            <a-col>
              <span style="font-weight: 600">{{ r.region_name }}</span>
              <span style="color: #6b7280; font-size: 12px; margin-left: 8px">
                ID #{{ r.region_id }} · DNS {{ r.ip_count }} 个 · 实时游戏 {{ r.game_ip_count }} 个 · {{ r.servers.length }} 个游戏服
              </span>
            </a-col>
            <a-col flex="auto" />
          </a-row>
        </template>

        <a-row :gutter="16">
          <a-col :xs="24" :md="8">
            <div style="color: #6b7280; font-size: 12px; margin-bottom: 4px">域名</div>
            <div v-for="d in r.domains" :key="d" style="font-family: ui-monospace, Menlo, monospace; font-size: 12px">
              {{ d }}
            </div>
          </a-col>
          <a-col :xs="24" :md="8">
            <div style="color: #6b7280; font-size: 12px; margin-bottom: 4px">DNS 解析 IP ({{ r.ip_count }})</div>
            <div style="max-height: 96px; overflow-y: auto; font-family: ui-monospace, Menlo, monospace; font-size: 11px">
              <a-tag v-for="ip in r.ips" :key="`dns-${ip}`" style="margin: 1px">{{ ip }}</a-tag>
              <span v-if="r.ips.length === 0" style="color: #9ca3af">暂无</span>
            </div>
          </a-col>
          <a-col :xs="24" :md="8">
            <div style="color: #6b7280; font-size: 12px; margin-bottom: 4px">实时发现游戏 IP ({{ r.game_ip_count }})</div>
            <div style="max-height: 96px; overflow-y: auto; font-family: ui-monospace, Menlo, monospace; font-size: 11px">
              <a-tag v-for="ip in r.game_ips" :key="`game-${ip}`" color="green" style="margin: 1px">{{ ip }}</a-tag>
              <span v-if="r.game_ips.length === 0" style="color: #9ca3af">暂无</span>
            </div>
          </a-col>
        </a-row>

        <a-divider style="margin: 12px 0" />

        <div style="color: #6b7280; font-size: 12px; margin-bottom: 4px">游戏服</div>
        <div v-if="r.servers.length === 0" style="color: #9ca3af; font-size: 12px">无游戏服数据</div>
        <a-row v-else :gutter="[8, 8]">
          <a-col v-for="s in r.servers" :key="s.server_id" :xs="24" :sm="12" :md="8" :lg="6">
            <div class="server-stat-card">
              <div class="server-stat-name">
                <strong>ID {{ s.server_id }}</strong> · {{ s.server_name }}
              </div>
              <div class="server-stat-counts">
                <a-tag color="blue">当前单节点最高 {{ s.current_connections ?? 0 }}</a-tag>
                <a-tag color="orange" title="只统计连续在线满 2 小时的游戏端口连接；按单个节点计算，不会把不同节点相加；此值不代表官方公布上限">
                  历史单节点峰值 {{ s.max_observed_connections ?? 0 }}
                </a-tag>
              </div>
            </div>
          </a-col>
        </a-row>
      </a-card>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { message } from 'ant-design-vue';
import {
  listRegionServers,
  syncRegions,
  type RegionBundle,
} from '../api/regions';

const allRegions = ref<RegionBundle[]>([]);
const loading = ref(false);
const syncing = ref(false);
const selectedRegionIds = ref<number[]>([]);
const serverQuery = ref('');
const refreshing = ref(false);
let refreshTimer: number | undefined;

// Read-only view. regions / region_servers tables are populated by
// `go run ./cmd/seedregions --with-servers` at deploy time; admin does
// not modify them at runtime.

const filteredRegions = computed(() => {
  let r = allRegions.value;
  if (selectedRegionIds.value.length > 0) {
    const set = new Set(selectedRegionIds.value);
    r = r.filter((x) => set.has(x.region_id));
  }
  const q = serverQuery.value.trim().toLowerCase();
  if (q) {
    r = r
      .map((x) => ({ ...x, servers: x.servers.filter((s) => s.server_name.toLowerCase().includes(q)) }))
      .filter((x) => x.servers.length > 0);
  }
  return r;
});

const totalServers = computed(() =>
  allRegions.value.reduce((acc, r) => acc + r.servers.length, 0),
);

const totalGameIPs = computed(() =>
  new Set(allRegions.value.flatMap((r) => r.game_ips || [])).size,
);

const reload = async (showLoading = true) => {
  if (refreshing.value) return;
  refreshing.value = true;
  if (showLoading) loading.value = true;
  try {
    const r = await listRegionServers();
    allRegions.value = Array.isArray(r.data) ? r.data : [];
  } catch (e: any) {
    if (showLoading) {
      message.error(e?.response?.data?.error?.message || '加载失败');
      allRegions.value = [];
    }
  } finally {
    if (showLoading) loading.value = false;
    refreshing.value = false;
  }
};

const askSync = async () => {
  syncing.value = true;
  try {
    const r = await syncRegions();
    const d = r.data || ({} as any);
    const ipChanged = (d.results || []).filter((x: any) => x.ip_changed).length;
    const serverChanged = (d.results || []).filter((x: any) => x.server_changed).length;
    const skipped = (d.results || []).filter((x: any) => x.skipped).length;
    message.success(
      `已同步 ${d.synced_count ?? 0} 个大区，IP 变更 ${ipChanged}，游戏服变更 ${serverChanged}` +
        (skipped > 0 ? `，跳过 ${skipped}` : ''),
    );
    await reload();
  } catch (e: any) {
    message.error(e?.response?.data?.error?.message || '同步失败');
  } finally {
    syncing.value = false;
  }
};

onMounted(() => {
  void reload();
  refreshTimer = window.setInterval(() => {
    void reload(false);
  }, 5000);
});

onUnmounted(() => {
  if (refreshTimer !== undefined) window.clearInterval(refreshTimer);
});
</script>

<style scoped>
.server-stat-card {
  height: 100%;
  padding: 8px 10px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: #fff;
}

.server-stat-name {
  margin-bottom: 6px;
  font-size: 12px;
}

.server-stat-counts {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.server-stat-counts :deep(.ant-tag) {
  margin-inline-end: 0;
}
</style>
